import { DateTime as DT } from 'luxon';
import LeadStatus from './LeadRows/LeadStatus';
import LeadAction from './LeadRows/LeadAction';
import LeadTitle from './LeadRows/LeadTitle';
import { Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModal } from 'context/ModalContext';
import { getLocale } from 'utils/helpers/functions/getLocale';
import { useLead } from './context/LeadProvider';
import type { LeadRowProps } from './types';
import { UserAvatarStack } from 'modules/user';
import { useAuth } from 'context/AuthContext';
import { isManagerUser } from 'utils/helpers/access/hasUserAccess';
import EToolTip from 'components/others/EToolTip';
import SolarIcon from 'components/icons/SolarIcon';

const LeadTableRow = ({ lead, fetchLeads, setClientInfoId, setTasksLeadId, setNewSale, onOpenAccess }: LeadRowProps) => {
    const { t, i18n } = useTranslation(['Leads', 'common']);
    const { auth } = useAuth();
    const { showTitle, showSource, showCreated, showAccess, showDate } = useLead();
    const canChangeAccess = isManagerUser(auth?.user);
    const { showDlg } = useModal();
    const locale = getLocale(i18n.language);
    const dtC = DT.fromMillis(lead?.leadCreated || 0).setLocale(locale).toLocaleString(DT.DATE_MED);
    const dtU = DT.fromMillis(lead?.leadUpdated || 0).setLocale(locale).toLocaleString(DT.DATE_MED);
    const sourceName = String(lead?.leadSourceName || '').trim();
    const sourceMessage = String(lead?.leadSourceMessage || '').trim();

    const showSourceMessage = () => {
        showDlg({
            type: 'info',
            title: t('fields.source'),
            text: (
                <>
                    <h6 className="mb-1">{sourceName || '-'}</h6>
                    <p className="mb-0 text-break">{sourceMessage || t('source.noMessage')}</p>
                </>
            ),
            buttons: [{ title: t('common:buttons.ok'), variant: 'primary' }],
        });
    };

    return (
        <tr>
            <td className="name">
                <Col>
                    <Link to={`/leads/${lead.ID}`}>
                        <h6 className="mb-1 cursor-pointer text-break">{lead.clientName}</h6>
                        <p className="text-muted f-12 mb-0 my-1">{lead.clientPhone}</p>
                    </Link>
                </Col>
                <Col className="d-block d-md-none">
                    <LeadStatus
                        leadStatus={lead?.leadStatus || 0}
                        leadPendingDt={lead?.leadPendingDt}
                    />
                </Col>
            </td>
            {showTitle ? (
                <td className="lead-title" title={String(lead.leadName || '').trim()}>
                        <LeadTitle
                        leadId={lead.ID}
                        leadName={lead.leadName}
                        readOnly={lead.myAccess === 'view'}
                        onSaved={() => {
                            void fetchLeads(true);
                        }}
                    />
                </td>
            ) : null}
            {showSource ? (
                <td className="source text-truncate">
                    {sourceName ? (
                        <button
                            type="button"
                            className="btn btn-link p-0 text-start text-truncate d-inline-block w-100"
                            onClick={showSourceMessage}
                            title={sourceMessage || t('source.noMessage')}
                        >
                            {sourceName}
                        </button>
                    ) : (
                        <span className="text-muted">-</span>
                    )}
                </td>
            ) : null}
            {showCreated ? (
                <td className="owner">
                    <UserAvatarStack
                        showNameForOne
                        items={[{
                            id: lead.ownerId,
                            name: lead.userName,
                            picture: lead.userPicture,
                        }]}
                    />
                </td>
            ) : null}
            {showAccess ? (
                <td className="access">
                    <div className="d-flex align-items-center gap-2">
                        <UserAvatarStack
                            items={(lead.accessUsers || []).map((grant) => ({
                                id: grant.ID,
                                name: grant.userName,
                                picture: grant.userPicture,
                                hint: `${grant.userName} — ${grant.accessLevel === 'edit' ? t('common:Users.accessLevelEdit') : t('common:Users.accessLevelView')}`,
                            }))}
                        />
                        {canChangeAccess ? (
                            <EToolTip title={t('common:buttons.change')}>
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 text-primary access-edit"
                                    onClick={() => onOpenAccess?.(lead)}
                                >
                                    <SolarIcon name="pencil" />
                                </button>
                            </EToolTip>
                        ) : null}
                    </div>
                </td>
            ) : null}
            <td className="status">
                <LeadStatus
                    leadStatus={lead?.leadStatus || 0}
                    leadPendingDt={lead?.leadPendingDt}
                />
            </td>
            {showDate ? (
                <td className="updated">
                    <h6 className="mb-1 cursor-pointer text-break">{dtC}</h6>
                    <p className="text-muted f-12 mb-0 my-1">{dtU}</p>
                </td>
            ) : null}
            <td className="action">
                <LeadAction
                    lead={lead}
                    fetchLeads={fetchLeads}
                    setClientInfoId={setClientInfoId}
                    setTasksLeadId={setTasksLeadId}
                    setNewSale={setNewSale}
                />
            </td>
        </tr>
    );
};

export default LeadTableRow;
